import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Banner,
  Button,
  Card,
  DataList,
  type DataListItem,
  Heading,
  LoadingAnimation,
  Menu,
  PageHeader,
  Stack,
} from "@knowtex-ai/components";
import {
  Download,
  EmptyPage,
  Hourglass,
  NavArrowLeft,
  Page as PageIcon,
} from "iconoir-react";
import { isActiveRun, type McEvent, type McRunDetail } from "../../lib/mc";
import {
  useMcTargetPlaybooksQuery,
  useMcRunEventsQuery,
  useMcRunQuery,
  useMcRunUsageQuery,
} from "../../hooks/useMcRuns";
import MarkdownText from "../Support/MarkdownText";
import CancelRunConfirmation from "./CancelRunConfirmation";
import McPageShell from "./McPageShell";
import { RunMemoryChanges } from "./McMemoryPage";
import NewTabLink from "./NewTabLink";
import { GAP, SectionHeading } from "./layout";
import RunArtifacts from "./RunArtifacts";
import { RunTriggerDetail } from "./RunTrigger";
import { RunResult, RunStatusBadge } from "./RunBadges";
import RunCost from "./RunCost";
import RunEvents from "./RunEvents";
import RunFindings from "./RunFindings";
import PlaybookChecksList from "./PlaybookChecksList";
import RunMissions from "./RunMissions";
import TimeCell from "./TimeCell";
import {
  formatCost,
  formatDuration,
  formatRunContext,
  playbookUrl,
  runPlaybook,
  runProblem,
} from "./format";
import {
  deriveMissions,
  findingDetails,
  maxBrowsers,
  memoryChanges,
} from "./runData";
import {
  useDownloadFindingsCsv,
  useDownloadRunBundle,
} from "./useRunDownloads";

function RunPlaybookSummary({ run }: { run: McRunDetail }) {
  const playbooks = useMcTargetPlaybooksQuery().data ?? [];
  const { playbookRevision, playbookDescription, playbookPurpose, playbookChecks, playbookBody } =
    run.input;
  const { title, playbook, custom, instructions } = runPlaybook(
    run.input,
    playbooks,
  );
  const description = custom
    ? "No playbook: the instructions are the whole run."
    : (playbookDescription ?? playbookPurpose ?? playbook?.description);
  return (
    <Stack gap={GAP.section}>
      <div className="flex flex-col gap-1">
        {custom || !playbook ? (
          <p className="text-sm font-medium text-gray-900">{title}</p>
        ) : (
          <NewTabLink to={playbookUrl(playbook.id, playbookRevision)}>
            {playbookRevision ? `${title}, version ${playbookRevision}` : title}
          </NewTabLink>
        )}
        {description && (
          <p className="whitespace-pre-wrap break-words text-sm text-gray-500">
            {description}
          </p>
        )}
      </div>
      {playbookChecks?.length ? (
        <PlaybookChecksList checks={playbookChecks} aria-label={`${title} checks`} />
      ) : (
        playbookBody && <MarkdownText text={playbookBody} />
      )}
      {instructions && (
        <section>
          <SectionHeading>Instructions</SectionHeading>
          <p className="whitespace-pre-wrap break-words text-sm text-gray-800">
            {instructions}
          </p>
        </section>
      )}
    </Stack>
  );
}

interface McRunDetailPageProps {
  setAuth: (isAuthenticated: boolean) => void;
  setUserOrg: (org: string | null) => void;
}

const runDetails = (run: McRunDetail, events: McEvent[]): DataListItem[] => {
  const browsers = maxBrowsers(events);
  const items: (DataListItem | false | undefined | "")[] = [
    {
      id: "runId",
      label: "Run ID",
      value: <span className="font-mono break-all">{run.id}</span>,
    },
    {
      id: "status",
      label: "Status",
      value: <RunStatusBadge status={run.status} />,
    },
    { id: "result", label: "Result", value: <RunResult run={run} /> },
    { id: "target", label: "Target", value: run.target },
    {
      id: "trigger",
      label: "Trigger",
      value: <RunTriggerDetail run={run} />,
    },
    { id: "requestedBy", label: "Triggered by", value: run.requestedBy || "—" },
    {
      id: "created",
      label: "Triggered",
      value: <TimeCell iso={run.createdAt} />,
    },
    {
      id: "started",
      label: "Started",
      value: <TimeCell iso={run.startedAt} />,
    },
    {
      id: "finished",
      label: "Finished",
      value: <TimeCell iso={run.finishedAt} />,
    },
    { id: "duration", label: "Duration", value: formatDuration(run) },
    { id: "cost", label: "Cost", value: formatCost(run) },
    {
      id: "browsers",
      label: "Browsers (max concurrent)",
      value: browsers ?? "—",
    },
    run.cancelRequestedBy && {
      id: "cancelBy",
      label: "Cancelled by",
      value: run.cancelRequestedBy,
    },
    run.cancelRequestedAt && {
      id: "cancelAt",
      label: "Cancel requested",
      value: <TimeCell iso={run.cancelRequestedAt} />,
    },
  ];
  return items.filter((item): item is DataListItem => Boolean(item));
};

export default function McRunDetailPage({
  setAuth,
  setUserOrg,
}: McRunDetailPageProps) {
  return (
    <McPageShell setAuth={setAuth} setUserOrg={setUserOrg}>
      <div className="mb-6">
        <Button variant="text" size="small" icon={NavArrowLeft} to="/mc/runs">
          All runs
        </Button>
      </div>
      <McRunView />
    </McPageShell>
  );
}

function McRunView() {
  const { runId = "" } = useParams();
  const runQuery = useMcRunQuery(runId);
  const run = runQuery.data;
  const active = isActiveRun(run);
  const eventsQuery = useMcRunEventsQuery(runId, { live: active });
  const usageQuery = useMcRunUsageQuery(runId, { live: active });
  const bundle = useDownloadRunBundle();
  const downloadFindings = useDownloadFindingsCsv();
  const [confirmCancel, setConfirmCancel] = useState(false);

  const events = useMemo(() => eventsQuery.data ?? [], [eventsQuery.data]);
  const missions = useMemo(() => deriveMissions(events), [events]);
  const findings = useMemo(
    () => findingDetails(run?.findings ?? [], events),
    [run?.findings, events],
  );
  const usage = usageQuery.data;

  const body = () => {
    if (runQuery.isLoading) {
      return (
        <div className="flex flex-col items-center justify-center gap-3 py-24 text-gray-400">
          <LoadingAnimation size={28} />
          <span className="text-sm">Loading run…</span>
        </div>
      );
    }

    if (!run) {
      const notFound = runQuery.error?.status === 404;
      return (
        <Banner
          variant="error"
          heading={notFound ? "Run not found" : "Couldn't load the run"}
        >
          {notFound ? `No MC run with id ${runId}.` : runQuery.error?.message}
        </Banner>
      );
    }

    const problem = runProblem(run);
    const exportFindings = () =>
      downloadFindings(run, eventsQuery.isSuccess ? { run, events } : undefined);

    return (
      <>
        <PageHeader
          title={`MC run ${run.id.slice(0, 8)}`}
          subtitle={formatRunContext(run)}
          actions={
            <>
              <Menu
                variant="secondary"
                icon={Download}
                align="end"
                disabled={active}
                items={[
                  {
                    key: "bundle",
                    label: bundle.isDownloading(run.id)
                      ? "Downloading run…"
                      : "Entire run (.zip)",
                    icon: Download,
                    disabled: bundle.isDownloading(run.id),
                    onClick: () => bundle.download(run.id),
                  },
                  {
                    key: "findings",
                    label: "Findings (.csv)",
                    icon: PageIcon,
                    onClick: exportFindings,
                  },
                ]}
              >
                Download
              </Menu>
              {active && (
                <Button
                  variant="secondary-danger"
                  disabled={!!run.cancelRequestedAt}
                  onClick={() => setConfirmCancel(true)}
                >
                  {run.cancelRequestedAt ? "Cancelling…" : "Cancel run"}
                </Button>
              )}
            </>
          }
        />

        <div className="mt-8">
          <Stack gap={GAP.page}>
            {runQuery.isRefetchError && (
              <Banner variant="warning" heading="Couldn't refresh the run">
                {runQuery.error.message}
              </Banner>
            )}
            {run.error && (
              <Banner
                variant={problem ? "error" : "info"}
                heading={problem ? "Error" : "Note"}
              >
                <span className="whitespace-pre-wrap break-words">
                  {run.error}
                </span>
              </Banner>
            )}

            <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
              <Card variant="elevated" title="Details">
                <DataList
                  items={runDetails(run, events)}
                  aria-label="Run details"
                />
              </Card>
              <Stack gap={GAP.page}>
                <Card variant="elevated" title="Playbook">
                  <RunPlaybookSummary run={run} />
                </Card>
                {run.summary ? (
                  <Card variant="elevated" title="Summary">
                    <MarkdownText text={run.summary} />
                  </Card>
                ) : (
                  <Card variant="inset" title="Summary">
                    <div className="flex flex-col items-center gap-2 py-6 text-center text-sm text-gray-500">
                      {active ? (
                        <Hourglass width={20} height={20} aria-hidden />
                      ) : (
                        <EmptyPage width={20} height={20} aria-hidden />
                      )}
                      <p>
                        {active
                          ? "MC writes the summary when the run finishes."
                          : "This run has no summary."}
                      </p>
                    </div>
                  </Card>
                )}
              </Stack>
            </div>

            <RunFindings
              runId={run.id}
              findings={findings}
              artifacts={run.artifacts ?? []}
              live={active}
              onExport={exportFindings}
            />
            <RunMemoryChanges changes={memoryChanges(events)} active={active} />
            <RunArtifacts runId={run.id} artifacts={run.artifacts ?? []} />

            <section
              aria-labelledby="run-internals"
              className="mt-4 border-t border-gray-200 pt-10"
            >
              <Stack gap={GAP.page}>
                <div>
                  <Heading as="h2" size="small">
                    <span id="run-internals">Run internals</span>
                  </Heading>
                  <p className="mt-1 text-sm text-gray-500">
                    Missions, events, and cost breakdown
                  </p>
                </div>
                <RunMissions
                  missions={missions}
                  live={active}
                  usage={usage?.byMission}
                />
                {eventsQuery.isError && (
                  <Banner variant="warning" heading="Couldn't load all events">
                    {eventsQuery.error.message}
                  </Banner>
                )}
                <RunEvents
                  runId={run.id}
                  events={events}
                  loading={eventsQuery.isLoading}
                  live={active}
                />
                <RunCost
                  run={run}
                  usage={usage}
                  loading={usageQuery.isLoading}
                />
              </Stack>
            </section>
          </Stack>
        </div>

        <CancelRunConfirmation
          run={confirmCancel ? run : null}
          onClose={() => setConfirmCancel(false)}
        />
      </>
    );
  };

  return body();
}
